import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { supabase } from "@/lib/supabase";

interface PhotoUploaderProps {
  value: string;
  onChange: (url: string) => void;
}

const BUCKET = "member-photos";
const MAX_SIZE = 5 * 1024 * 1024;

export default function PhotoUploader({ value, onChange }: PhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const upload = async (file: File) => {
    setError("");
    if (!file.type.startsWith("image/")) {
      setError("请选择图片文件（JPG / PNG / WebP）");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("图片大小不能超过 5MB");
      return;
    }

    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const safeExt = /^[a-z0-9]+$/.test(ext) ? ext : "jpg";
      const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      onChange(data.publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传失败，请稍后重试");
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void upload(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void upload(file);
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div className="relative h-40 w-32 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100">
        {value ? (
          <img
            src={value}
            alt="会员照片"
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-foreground-300">
            <i className="ri-user-3-line text-3xl"></i>
            <span className="text-xs">暂无照片</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/40">
            <i className="ri-loader-4-line animate-spin text-xl text-background-50"></i>
          </div>
        )}
      </div>

      <div className="flex-1">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            dragging ? "border-primary-400 bg-primary-50" : "border-background-300 bg-background-50"
          }`}
        >
          <p className="text-xs text-foreground-500">拖拽照片到此处，或</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-upload-2-line"></i>
              {value ? "替换照片" : "上传照片"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => {
                  setError("");
                  onChange("");
                }}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                <i className="ri-delete-bin-line"></i>
                移除
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-foreground-400">支持 JPG / PNG / WebP，单张不超过 5MB</p>
        </div>
        {error && <p className="mt-2 text-xs text-accent-600">{error}</p>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}